import React, { useMemo } from 'react';
import { Flame, AlertCircle, Plus, Trash2, Calendar, ArrowRightCircle, Wallet, TrendingDown } from 'lucide-react';
import { Card } from '../ui/Card';
import { SectionHeader } from '../ui/SectionHeader';
import { InputGroup } from '../ui/InputGroup';
import { MasterTipsCard } from '../ui/MasterTipsCard';
import { formatCurrency } from '../../utils/finance';

export const ExpensesTab = ({ data, actions, calculations }) => {
    const { expenseAnalysis, calculatedPaycheck, surplusCalculations } = calculations;

    // Check for expired sinking fund expenses
    const expiredExpenses = useMemo(() => {
        return data.expenseItems.filter(item => {
            if (!item.linkedSinkingFundId) return false;
            const fund = data.sinkingFunds.find(f => f.id === item.linkedSinkingFundId);
            if (!fund || !fund.date) return false;
            return new Date(fund.date) < new Date();
        });
    }, [data.expenseItems, data.sinkingFunds]);

    const monthlyIncome = calculatedPaycheck.monthlyNet || 0;
    const totalExpenses = expenseAnalysis.total || 0;
    const surplus = surplusCalculations.operational || 0;
    
    // Burn Rate Calculation for the Visual Bar
    const burnRate = monthlyIncome > 0 ? Math.min(100, (totalExpenses / monthlyIncome) * 100) : 0;

    return (
        <div className="animate-fade-in">
            
            {/* --- MASTER TIPS CARD (Replaces Header & TabInfo) --- */}
            <MasterTipsCard 
                title="Fixed vs. Variable" 
                subtitle="What do you burn to keep the engine running?" 
                element="fire"
            >
                Fire represents Burn. List your <strong>Fixed Monthly Commitments</strong> here (Rent, Insurance, Subscriptions). 
                Do <em>not</em> list variable spending (like Groceries or Dining) here; track those in the <strong>Tracker</strong> tab against your discretionary budget.
            </MasterTipsCard>

            <div className="grid grid-cols-1 gap-6">
                
                {/* 1. THE BURN GAUGE (Top Analysis) */}
                <Card element="fire" className="bg-gradient-to-r from-orange-900/40 to-slate-900 border-orange-500/20">
                    <div className="flex flex-col md:flex-row justify-between items-end mb-4">
                        <div>
                            <h3 className="text-xl font-bold text-white flex items-center gap-2">
                                <Wallet className="w-6 h-6 text-orange-400" /> Monthly Cash Flow
                            </h3>
                            <p className="text-sm text-slate-400">Net Income vs. Fixed Burn</p>
                        </div>
                        <div className="text-right">
                            <span className="text-xs font-bold uppercase tracking-widest text-slate-500">Burn Rate</span>
                            <p className={`text-2xl font-bold ${burnRate > 90 ? 'text-red-500' : 'text-orange-400'}`}>
                                {burnRate.toFixed(1)}%
                            </p>
                        </div>
                    </div>

                    {/* Visual Burn Bar */}
                    <div className="h-6 w-full bg-slate-950 rounded-full border border-slate-800 overflow-hidden relative mb-6">
                        <div 
                            className="h-full bg-gradient-to-r from-orange-600 to-red-500 transition-all duration-1000 ease-out relative"
                            style={{ width: `${burnRate}%` }}
                        >
                            <div className="absolute inset-0 bg-[url('https://www.transparenttextures.com/patterns/diagonal-stripes.png')] opacity-20"></div>
                        </div>
                        {/* Marker for Income Limit */}
                        <div className="absolute top-0 bottom-0 right-0 w-0.5 bg-emerald-500 shadow-[0_0_10px_#10b981]"></div>
                    </div>

                    {/* Stats Grid */}
                    <div className="grid grid-cols-3 gap-4 divide-x divide-slate-800">
                        <div className="px-4 text-center">
                            <p className="text-[10px] text-slate-500 uppercase font-bold mb-1">Net Income</p>
                            <p className="text-xl md:text-2xl font-bold text-white">{formatCurrency(monthlyIncome)}</p>
                        </div>
                        <div className="px-4 text-center">
                            <p className="text-[10px] text-orange-500 uppercase font-bold mb-1">Total Burn</p>
                            <p className="text-xl md:text-2xl font-bold text-orange-400">-{formatCurrency(totalExpenses)}</p>
                        </div>
                        <div className="px-4 text-center">
                            <p className="text-[10px] text-blue-500 uppercase font-bold mb-1">Surplus</p>
                            <p className={`text-xl md:text-2xl font-bold ${surplus >= 0 ? 'text-blue-400' : 'text-red-400'}`}>
                                {formatCurrency(surplus)}
                            </p>
                        </div>
                    </div>
                </Card>

                {/* 2. EXPENSE ITEMS LIST */}
                <Card element="fire">
                    <div className="flex justify-between items-center mb-6">
                        <SectionHeader icon={Flame} title="Expense Items" subtitle="Recurring monthly commitments" element="fire" />
                        <button onClick={() => actions.addArrayItem('expenseItems', { id: Date.now(), name: "New Expense", amount: 0, frequency: "monthly", type: "Fixed", dayDue: 1 })} className="bg-orange-600 hover:bg-orange-500 text-slate-900 text-sm font-bold px-4 py-2 rounded-lg flex items-center gap-2 transition-colors shadow-lg shadow-orange-900/20">
                            <Plus className="w-4 h-4" /> Add Item
                        </button>
                    </div>
                    
                    {/* Alerts for Expired Funds */}
                    {expiredExpenses.length > 0 && (
                        <div className="mb-6 space-y-2">
                            {expiredExpenses.map(ex => (
                                <div key={ex.id} className="bg-red-500/10 border border-red-500/50 p-3 rounded-lg flex items-center gap-3 text-xs">
                                    <AlertCircle className="w-4 h-4 text-red-500" />
                                    <div className="flex-1 text-slate-300">
                                        <strong className="text-red-400">{ex.name}</strong> target date reached. Remove from burn?
                                    </div>
                                    <button onClick={() => actions.removeArrayItem('expenseItems', ex.id)} className="bg-red-500/20 text-red-400 px-3 py-1 rounded hover:bg-red-500/30 font-bold">Remove</button>
                                </div>
                            ))}
                        </div>
                    )}

                    <div className="space-y-3">
                        {data.expenseItems.map((item) => (
                            <div key={item.id} className="group bg-slate-950 border border-slate-800 rounded-xl p-4 transition-all hover:border-orange-500/40 hover:shadow-lg hover:shadow-orange-900/10">
                                
                                {/* DESKTOP LAYOUT (Grid) */}
                                <div className="hidden lg:grid grid-cols-12 gap-4 items-center">
                                    {/* Name (Cols 1-4) */}
                                    <div className="col-span-4">
                                        <input 
                                            value={item.name} 
                                            onChange={(e) => actions.updateArrayItem('expenseItems', item.id, 'name', e.target.value)} 
                                            className="bg-transparent font-bold text-lg text-white outline-none w-full placeholder-slate-600" 
                                            placeholder="Expense Name"
                                        />
                                        <div className="flex gap-2 mt-1">
                                            {item.linkedSinkingFundId && <span className="text-[9px] text-blue-300 bg-blue-900/30 px-1.5 py-0.5 rounded border border-blue-500/30">Reservoir Link</span>}
                                            {item.linkedDebtId && <span className="text-[9px] text-orange-300 bg-orange-900/30 px-1.5 py-0.5 rounded border border-orange-500/30">Debt Link</span>}
                                        </div>
                                    </div>

                                    {/* Amount & Freq (Cols 5-7) */}
                                    <div className="col-span-3 flex items-center gap-2">
                                        <div className="relative flex-1">
                                            <span className="absolute left-3 top-2.5 text-slate-500 text-xs font-bold">$</span>
                                            <input 
                                                type="number" 
                                                value={item.amount} 
                                                onChange={(e) => actions.updateArrayItem('expenseItems', item.id, 'amount', parseFloat(e.target.value))} 
                                                className="bg-slate-900 border border-slate-700 rounded-lg w-full py-2 pl-6 pr-2 text-white font-mono font-bold outline-none focus:border-orange-500" 
                                            />
                                        </div>
                                        <select 
                                            value={item.frequency} 
                                            onChange={(e) => actions.updateArrayItem('expenseItems', item.id, 'frequency', e.target.value)} 
                                            className="bg-slate-900 border border-slate-700 rounded-lg py-2 px-1 text-xs text-slate-300 outline-none w-20"
                                        >
                                            <option value="monthly">/ Mo</option>
                                            <option value="bi-weekly">/ 2 Wk</option>
                                            <option value="weekly">/ Wk</option>
                                            <option value="annually">/ Yr</option>
                                        </select>
                                    </div>

                                    {/* Timing (Cols 8-9) */}
                                    <div className="col-span-2">
                                        {item.frequency === 'monthly' && (
                                            <div className="flex items-center gap-2 bg-slate-900 rounded-lg border border-slate-800 p-1.5">
                                                <Calendar className="w-3 h-3 text-slate-500"/>
                                                <span className="text-[10px] text-slate-500 whitespace-nowrap">Day</span>
                                                <input 
                                                    type="number" min="1" max="31"
                                                    value={item.dayDue || 1} 
                                                    onChange={(e) => actions.updateArrayItem('expenseItems', item.id, 'dayDue', parseInt(e.target.value))} 
                                                    className="bg-transparent w-full text-center text-sm font-bold text-white outline-none"
                                                />
                                            </div>
                                        )}
                                        {item.frequency === 'bi-weekly' && (
                                            <select 
                                                value={item.timing || 'payday'} 
                                                onChange={(e) => actions.updateArrayItem('expenseItems', item.id, 'timing', e.target.value)} 
                                                className="bg-slate-900 border border-slate-700 rounded-lg p-2 text-xs text-slate-300 w-full outline-none"
                                            >
                                                <option value="payday">Match Pay</option>
                                                <option value="custom">Custom</option>
                                            </select>
                                        )}
                                    </div>

                                    {/* Flow To (Cols 10-11) */}
                                    <div className="col-span-2">
                                        <select 
                                            value={item.linkedAssetId || ""} 
                                            onChange={(e) => actions.updateArrayItem('expenseItems', item.id, 'linkedAssetId', Number(e.target.value) || null)} 
                                            className={`bg-slate-900 border rounded-lg p-2 text-xs w-full outline-none ${item.linkedAssetId ? 'border-emerald-500/50 text-emerald-400 font-bold' : 'border-slate-800 text-slate-500'}`}
                                        >
                                            <option value="">(Burn)</option>
                                            {data.assets.map(a => <option key={a.id} value={a.id}>→ {a.name}</option>)}
                                        </select>
                                    </div>

                                    {/* Delete (Col 12) */}
                                    <div className="col-span-1 flex justify-end">
                                        <button onClick={() => actions.removeArrayItem('expenseItems', item.id)} className="text-slate-600 hover:text-red-400 p-2 hover:bg-slate-800 rounded-lg transition-colors">
                                            <Trash2 className="w-4 h-4" />
                                        </button>
                                    </div>
                                </div>

                                {/* MOBILE LAYOUT (Stack) */}
                                <div className="lg:hidden space-y-4">
                                    <div className="flex justify-between items-start">
                                        <div className="flex-1">
                                            <input 
                                                value={item.name} 
                                                onChange={(e) => actions.updateArrayItem('expenseItems', item.id, 'name', e.target.value)} 
                                                className="bg-transparent font-bold text-lg text-white outline-none w-full" 
                                                placeholder="Expense Name"
                                            />
                                            <div className="flex gap-2 mt-1">
                                                {item.linkedSinkingFundId && <span className="text-[10px] text-blue-300">Linked to Reservoir</span>}
                                            </div>
                                        </div>
                                        <button onClick={() => actions.removeArrayItem('expenseItems', item.id)} className="text-slate-600 hover:text-red-400">
                                            <Trash2 className="w-4 h-4" />
                                        </button>
                                    </div>

                                    <div className="flex gap-2">
                                        <div className="relative flex-1">
                                            <span className="absolute left-3 top-2.5 text-slate-500 text-xs">$</span>
                                            <input 
                                                type="number" 
                                                value={item.amount} 
                                                onChange={(e) => actions.updateArrayItem('expenseItems', item.id, 'amount', parseFloat(e.target.value))} 
                                                className="bg-slate-900 border border-slate-700 rounded-lg w-full py-2 pl-6 pr-2 text-white font-mono text-sm outline-none" 
                                            />
                                        </div>
                                        <select 
                                            value={item.frequency} 
                                            onChange={(e) => actions.updateArrayItem('expenseItems', item.id, 'frequency', e.target.value)} 
                                            className="bg-slate-900 border border-slate-700 rounded-lg py-2 px-2 text-xs text-slate-300 outline-none"
                                        >
                                            <option value="monthly">/ Mo</option>
                                            <option value="bi-weekly">/ 2 Wk</option>
                                            <option value="annually">/ Yr</option>
                                        </select>
                                    </div>

                                    <div className="grid grid-cols-2 gap-2">
                                        {/* Mobile Timing */}
                                        {item.frequency === 'monthly' && (
                                            <div className="flex items-center gap-2 bg-slate-900 rounded-lg border border-slate-800 p-2">
                                                <Calendar className="w-3 h-3 text-slate-500"/>
                                                <span className="text-xs text-slate-500">Day Due:</span>
                                                <input 
                                                    type="number" min="1" max="31"
                                                    value={item.dayDue || 1} 
                                                    onChange={(e) => actions.updateArrayItem('expenseItems', item.id, 'dayDue', parseInt(e.target.value))} 
                                                    className="bg-transparent w-full text-sm font-bold text-white outline-none"
                                                />
                                            </div>
                                        )}
                                        {/* Mobile Flow */}
                                        <select 
                                            value={item.linkedAssetId || ""} 
                                            onChange={(e) => actions.updateArrayItem('expenseItems', item.id, 'linkedAssetId', Number(e.target.value) || null)} 
                                            className={`bg-slate-900 border rounded-lg p-2 text-xs w-full outline-none ${item.linkedAssetId ? 'border-emerald-500/50 text-emerald-400' : 'border-slate-800 text-slate-500'}`}
                                        >
                                            <option value="">(Burn)</option>
                                            {data.assets.map(a => <option key={a.id} value={a.id}>→ {a.name}</option>)}
                                        </select>
                                    </div>
                                </div>

                            </div>
                        ))}
                    </div>
                </Card>
            </div>
        </div>
    );
};